import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetch, Panel, Stat, Tag, PageHeader, pnlClass, Empty, dirTone } from "@/components/Common";
import { api, errMsg, fmtMoney, fmtPrice, fmtTime } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const SYMS = ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCHF", "USDCAD", "XAUUSD", "AAPL", "MSFT", "NVDA", "TSLA", "AMZN", "SPY"];
const FIELDS = [["min_score", "Min score"], ["atr_sl", "ATR stop ×"], ["rr", "Reward:Risk"], ["risk_pct", "Risk %"], ["balance", "Start balance"], ["slippage_atr", "Slippage ×ATR"], ["oos_pct", "Out-of-sample %"], ["wf_folds", "WF folds"]];
const STRATS = ["all", "trend_pullback", "breakout", "mean_reversion", "momentum"];

const Mini = ({ title, m, testid }) => (
  <div className="rounded border p-4" data-testid={testid}>
    <div className="label">{title}</div>
    <div className={`num mt-1 text-xl ${pnlClass(m.net_pnl)}`}>{fmtMoney(m.net_pnl)}</div>
    <div className="mt-1 font-mono text-xs text-muted-foreground">{m.trades} trades · {m.win_rate}% · PF {m.profit_factor ?? "—"} · DD {m.max_drawdown_pct}% · t {m.t_stat ?? "—"}</div>
    <div className="mt-1"><Tag tone={m.statistically_meaningful ? "up" : "warn"}>{m.statistically_meaningful ? "statistically meaningful" : "not statistically meaningful"}</Tag></div>
  </div>
);

function Validation({ r }) {
  if (!r.walk_forward) return null;
  return (
    <Panel title="Validation — in-sample vs out-of-sample vs walk-forward" testid="bt-validation">
      <div className="grid gap-3 md:grid-cols-3">
        <Mini title={`In-sample (to ${fmtTime(r.oos_from)})`} m={r.in_sample} testid="bt-in-sample" />
        <Mini title="Out-of-sample (unseen)" m={r.out_of_sample} testid="bt-out-of-sample" />
        <Mini title="Walk-forward OOS (re-optimised per fold)" m={r.walk_forward.metrics} testid="bt-walk-forward" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">{r.walk_forward.folds.map((f) => <Tag key={f.fold} tone={f.test.net_pnl >= 0 ? "up" : "down"}>fold {f.fold}: score≥{f.chosen_min_score} · {f.test.trades}t · {fmtMoney(f.test.net_pnl)}</Tag>)}</div>
      <div className="mt-3 flex flex-wrap gap-2">{Object.entries(r.by_strategy || {}).map(([k, v]) => <Tag key={k} tone={v.pnl >= 0 ? "up" : "down"}>{k}: {v.trades}t {fmtMoney(v.pnl)}</Tag>)}</div>
      <p className="mt-3 text-xs text-muted-foreground">{r.cost_model} Costs in this run: {fmtMoney(r.metrics.costs)}. Past results do not imply future profitability.</p>
    </Panel>
  );
}

function Proposals() {
  const { user } = useAuth();
  const { data, reload } = useFetch("/strategy/proposals");
  const [sym, setSym] = useState("EURUSD");
  const [busy, setBusy] = useState(false);
  const gen = async () => { setBusy(true); try { await api.post("/strategy/proposals", { symbol: sym, interval: "1h" }); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const decide = async (id, approve) => { try { await api.post(`/strategy/proposals/${id}/decision`, { approve }); toast.success(approve ? "Promoted to live engine rules" : "Rejected"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Strategy proposals (Owner approval required)" testid="strategy-proposals-panel" right={
      <div className="flex gap-2">
        <Select value={sym} onValueChange={setSym}><SelectTrigger className="w-32" data-testid="proposal-symbol-select"><SelectValue /></SelectTrigger><SelectContent>{SYMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" onClick={gen} disabled={busy} data-testid="proposal-generate-button">{busy && <Loader2 size={12} className="mr-1 animate-spin" />}Generate</Button>
      </div>}>
      <p className="mb-3 text-xs text-muted-foreground">The system proposes parameter changes from walk-forward evidence. Nothing is applied to live execution until it passes validation and the Owner approves it.</p>
      {data?.length ? data.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 py-2 text-sm" data-testid={`proposal-${p.id}`}>
          <span className="font-mono">{p.symbol} {p.interval}</span>
          <span className="font-mono text-xs">min_score {p.current.min_tech_score} → {p.proposed.min_tech_score}</span>
          <span className="font-mono text-xs text-muted-foreground">WF {p.evidence.walk_forward.trades}t · PF {p.evidence.walk_forward.profit_factor ?? "—"} · {fmtMoney(p.evidence.walk_forward.net_pnl)}</span>
          <Tag tone={p.validation === "validated" ? "up" : "warn"}>{p.validation}</Tag><Tag>{p.status}</Tag>
          {p.status === "pending_owner_approval" && user.role === "owner" && <span className="flex gap-1"><Button size="sm" onClick={() => decide(p.id, true)} data-testid={`proposal-approve-${p.id}`}>Approve</Button><Button size="sm" variant="ghost" onClick={() => decide(p.id, false)}>Reject</Button></span>}
        </div>
      )) : <Empty>No proposals yet</Empty>}
    </Panel>
  );
}

export function Results({ r }) {
  const m = r.metrics;
  return (
    <div className="space-y-4" data-testid="backtest-results">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Net P/L" value={fmtMoney(m.net_pnl)} tone={pnlClass(m.net_pnl)} testid="bt-net-pnl" />
        <Stat label="Return" value={`${m.return_pct}%`} tone={pnlClass(m.return_pct)} />
        <Stat label="Trades" value={m.trades} testid="bt-trades" />
        <Stat label="Win rate" value={`${m.win_rate}%`} />
        <Stat label="Profit factor" value={m.profit_factor ?? "—"} />
        <Stat label="Max DD" value={`${m.max_drawdown_pct}%`} tone="text-down" />
      </div>
      <Panel title={`Equity · ${r.symbol} ${r.interval} · ${r.source}`} right={<span className="font-mono text-xs text-muted-foreground">{fmtTime(r.from)} → {fmtTime(r.to)}</span>}>
        {r.equity_curve.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={r.equity_curve}><XAxis dataKey="time" hide /><YAxis domain={["auto", "auto"]} width={70} tick={{ fill: "#94a3b8", fontSize: 11 }} /><Tooltip contentStyle={{ background: "#121826", border: "1px solid #2a3142" }} /><Line dataKey="equity" stroke="#3b82f6" dot={false} strokeWidth={2} /></LineChart>
          </ResponsiveContainer>
        ) : <Empty>No trades triggered with these parameters</Empty>}
      </Panel>
      <Panel title="Trade list">
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-sm"><thead><tr className="label text-left"><th>Opened</th><th>Side</th><th>Entry</th><th>Exit</th><th>Result</th><th>P/L</th></tr></thead>
            <tbody>{[...r.trades].reverse().map((t, i) => (
              <tr key={i} className="border-t border-border/50"><td className="py-1.5 font-mono text-xs">{fmtTime(t.opened)}</td><td><Tag tone={dirTone(t.direction)}>{t.direction}</Tag></td><td className="num">{fmtPrice(t.entry, r.symbol)}</td><td className="num">{fmtPrice(t.exit, r.symbol)}</td><td><Tag tone={t.result === "tp" ? "up" : "down"}>{t.result}</Tag></td><td className={`num ${pnlClass(t.pnl)}`}>{fmtMoney(t.pnl)}</td></tr>
            ))}</tbody></table>
        </div>
      </Panel>
    </div>
  );
}

export default function Backtest() {
  const [f, setF] = useState({ symbol: "EURUSD", interval: "1h", strategy: "all", min_score: 55, atr_sl: 1.5, rr: 2, risk_pct: 1, balance: 10000, slippage_atr: 0.05, oos_pct: 30, wf_folds: 4 });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const { data: history, reload } = useFetch("/backtests");
  const run = async () => {
    setBusy(true);
    try {
      const body = { ...f }; FIELDS.forEach(([k]) => (body[k] = Number(body[k])));
      const { data } = await api.post("/backtest", body); setRes(data); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const open = async (id) => { try { setRes((await api.get(`/backtests/${id}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <PageHeader title="Backtesting" sub="Same strategy rules as the live engine on genuine historical candles — net of spread, commission and slippage, with out-of-sample and walk-forward validation" />
      <Panel title="Parameters">
        <div className="grid gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <Select value={f.symbol} onValueChange={(v) => setF({ ...f, symbol: v })}><SelectTrigger data-testid="bt-symbol-select"><SelectValue /></SelectTrigger><SelectContent>{SYMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select value={f.interval} onValueChange={(v) => setF({ ...f, interval: v })}><SelectTrigger data-testid="bt-interval-select"><SelectValue /></SelectTrigger><SelectContent>{["15min", "1h", "4h", "1day"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select value={f.strategy} onValueChange={(v) => setF({ ...f, strategy: v })}><SelectTrigger data-testid="bt-strategy-select"><SelectValue /></SelectTrigger><SelectContent>{STRATS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          {FIELDS.map(([k, l]) => <div key={k}><div className="label mb-1">{l}</div><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="font-mono" data-testid={`bt-${k}-input`} /></div>)}
          <Button onClick={run} disabled={busy} className="self-end" data-testid="bt-run-button">{busy && <Loader2 size={14} className="mr-1 animate-spin" />}Run backtest</Button>
        </div>
      </Panel>
      {res && <Results r={res} />}
      {res && <Validation r={res} />}
      <Proposals />
      <Panel title="Previous runs">
        {history?.length ? history.map((h) => (
          <button key={h.id} onClick={() => open(h.id)} className="flex w-full items-center justify-between border-b border-border/50 py-2 text-left text-sm transition-colors hover:bg-secondary/50" data-testid={`bt-history-${h.id}`}>
            <span className="font-mono">{h.symbol} {h.interval}</span><span className="text-xs text-muted-foreground">{h.metrics.trades} trades · {h.metrics.win_rate}% · PF {h.metrics.profit_factor ?? "—"}</span>
            <span className={`num ${pnlClass(h.metrics.net_pnl)}`}>{fmtMoney(h.metrics.net_pnl)}</span><span className="font-mono text-xs text-muted-foreground">{fmtTime(h.created_at)}</span>
          </button>
        )) : <Empty>No runs yet</Empty>}
      </Panel>
    </div>
  );
}
